import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { CheckCircle, ArrowRight, Shield, Zap, Clock, Headphones } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ProductDetailPage = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/api/products`);
        const found = response.data.find(p => p.id === productId);
        setProduct(found);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 flex items-center justify-center">
        <div className="text-white text-xl">جاري التحميل...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 flex items-center justify-center">
        <div className="text-center">
          <div className="text-white text-2xl mb-4">الباقة غير موجودة</div>
          <Link to="/plans">
            <Button className="bg-red-500 hover:bg-red-600">العودة للباقات</Button>
          </Link>
        </div>
      </div>
    );
  }

  const getDurationLabel = () => {
    if (product.duration === "شهري") return "شهرياً";
    return `لمدة ${product.duration} - دفع مرة واحدة`;
  };

  const highlights = [
    { icon: Shield, title: "حماية كاملة", desc: "SSL مجاني وحماية من الهجمات" },
    { icon: Zap, title: "سرعة فائقة", desc: "سيرفرات SSD عالية الأداء" },
    { icon: Clock, title: "نسخ احتياطي", desc: "نسخ احتياطي يومي تلقائي" },
    { icon: Headphones, title: "دعم فني", desc: "دعم فني متاح على مدار الساعة" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
      <Navbar />

      <section className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-gray-400 mb-8 text-sm">
            <Link to="/" className="hover:text-white">الرئيسية</Link>
            <span>/</span>
            <Link to="/plans" className="hover:text-white">الباقات</Link>
            <span>/</span>
            <span className="text-white">{product.name_ar}</span>
          </div>

          <div className="grid lg:grid-cols-3 gap-10">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
                <Badge className="bg-red-500/20 text-red-300 border-red-500/30 mb-4">
                  {product.duration === "شهري" ? "اشتراك شهري" : `اشتراك ${product.duration}`}
                </Badge>

                <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                  {product.name_ar}
                </h1>

                <p className="text-xl text-gray-300 mb-8">
                  {product.description_ar}
                </p>

                {/* Highlights */}
                <div className="grid sm:grid-cols-2 gap-4 mb-10">
                  {highlights.map((h, i) => (
                    <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-4 flex items-start gap-3">
                      <div className="bg-red-500/20 p-2 rounded-lg">
                        <h.icon className="h-5 w-5 text-red-400" />
                      </div>
                      <div>
                        <h4 className="text-white font-semibold">{h.title}</h4>
                        <p className="text-gray-400 text-sm">{h.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Features */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-white mb-4">مميزات الباقة</h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {product.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-gray-300">
                        <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Description Section */}
                <div className="mt-8 bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-white mb-4">تفاصيل الباقة</h3>
                  <div className="text-gray-300 space-y-3 leading-relaxed">
                    <p>
                      باقة <strong className="text-white">{product.name_ar}</strong> توفر لك كل ما تحتاجه لإطلاق موقعك على الإنترنت بأعلى جودة وأفضل أداء.
                    </p>
                    <p>
                      مع سيرفرات SSD فائقة السرعة وشهادة SSL مجانية، موقعك هيكون سريع وآمن. كمان بنوفرلك نسخ احتياطي يومي عشان بياناتك تكون في أمان دايماً.
                    </p>
                    {product.duration !== "شهري" && (
                      <p>
                        الباقة دي بتتدفع <strong className="text-white">مرة واحدة لمدة {product.duration}</strong>، وده بيوفرلك أكتر مقارنة بالدفع الشهري.
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Sidebar - Price Card */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="sticky top-24"
              >
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <div className="text-center mb-6">
                    <div className="text-5xl font-bold text-white mb-2">
                      {product.price_total.toLocaleString()} <span className="text-2xl">ج.م</span>
                    </div>
                    <p className="text-gray-400">{getDurationLabel()}</p>
                    <p className="text-sm text-gray-500 mt-1">{product.price_eur}€</p>
                  </div>

                  <Link to={`/checkout?product=${product.id}&cycle=${product.duration === "شهري" ? "monthly" : "yearly"}`} className="block">
                    <Button className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-6 text-lg">
                      اشترِ الآن
                      <ArrowRight className="h-5 w-5 mr-2" />
                    </Button>
                  </Link>

                  <div className="mt-6 space-y-3">
                    <div className="flex items-center gap-2 text-gray-300 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-400" />
                      <span>ضمان استرداد خلال 30 يوم</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-300 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-400" />
                      <span>تفعيل فوري بعد الدفع</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-300 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-400" />
                      <span>دعم فني مجاني</span>
                    </div>
                  </div>
                </div>

                {/* Back to Plans */}
                <Link to="/plans" className="block mt-4">
                  <Button variant="outline" className="w-full border-white/20 text-gray-300 hover:text-white">
                    العودة لجميع الباقات
                  </Button>
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
